// Shared, deterministic SkillBridge DISPLAY helpers used by both the Opportunities
// list (JobBoard) and the opportunity detail page (JobDetails).
// NO AI, NO summarization, NO rewriting of substantive source text — presentation only.
// The underlying stored `jobs` / `skillbridge_source_records` are never modified.

const _TRIM_SEP = (s) => s.replace(/[\s\-–—|:]+$/, '').trim();

// Strip trailing program boilerplate (incl. the program type and a "SkillBridge"
// tag) from a single title segment.
function _stripBoilerplate(seg, ptype) {
  // Only treat program_type as strippable when it is a GENERIC program label
  // (e.g. "Job Training Program", "On the Job Training") — never a distinctive role.
  const ptypeGeneric = /\b(program|training|internship|fellowship|apprenticeship|employment)\b/i.test(ptype) || /on the job/i.test(ptype);
  const tokens = [ptypeGeneric ? ptype : null, 'Job Training Program', 'Internship Program',
    'Fellowship Program', 'Internship', 'Fellowship', 'Employment',
    'SkillBridge', 'Skillbridge'].filter(Boolean);
  let s = seg.trim();
  let changed = true;
  while (changed) {
    changed = false;
    s = _TRIM_SEP(s);
    for (const t of tokens) {
      if (t && s.toLowerCase().endsWith(t.toLowerCase())) {
        s = _TRIM_SEP(s.slice(0, s.length - t.length));
        changed = true;
      }
    }
  }
  return s;
}

// Clean an imported SkillBridge title: split on " - " separators, strip boilerplate
// from each part, drop repeated segments, then strip a leading provider name. Surfaces
// provider + program type separately. Falls back to provider → "SkillBridge Opportunity".
export function skillbridgeDisplay(job) {
  const provider = (job.employer_name || '').trim();
  const meta = job.skillbridge_meta || {};
  const ptype = (meta.program_type || '').trim();
  const rawTitle = (job.title || '').replace(/\s+/g, ' ').trim();

  // De-duplicate repeated " - " segments (e.g. "X-Skillbridge - X" → "X").
  const segs = rawTitle.split(/\s+[-–—|]\s+/).map((s) => _stripBoilerplate(s, ptype)).filter(Boolean);
  const seen = new Set();
  const kept = [];
  for (const s of segs) {
    const k = s.toLowerCase();
    if (!seen.has(k)) { seen.add(k); kept.push(s); }
  }
  let title = kept.join(' - ');

  // Strip a leading provider name (with or without a trailing legal suffix).
  const provNoSuffix = provider.replace(/,?\s+(LLC|Inc\.?|L\.?L\.?C\.?|PLLC|Corp\.?|Co\.?|Ltd\.?|LP|LLP)\.?$/i, '').trim();
  for (const p of [provider, provNoSuffix].filter(Boolean)) {
    if (title.toLowerCase().startsWith(p.toLowerCase())) {
      title = title.slice(p.length).replace(/^[\s\-–—|:,]+/, '').trim();
    }
  }
  // Drop an orphaned leading legal suffix left behind (e.g. "..., Inc" → "Inc").
  title = title.replace(/^(inc|llc|l\.?l\.?c|pllc|corp|co|ltd|lp|llp)\.?\b[\s,\-–—|:]*/i, '').trim();

  const distinctive = title.length >= 3 ? title : '';
  const displayTitle = distinctive || provider || 'SkillBridge Opportunity';
  const showProvider = !!provider && provider.toLowerCase() !== displayTitle.toLowerCase();
  const showProgramType = !!ptype && ptype.toLowerCase() !== displayTitle.toLowerCase();
  const summary = (meta.summary || job.description || '').replace(/\s+/g, ' ').trim();
  return { displayTitle, provider, showProvider, summary, programType: ptype, showProgramType };
}

// ── Section mapping helpers (detail page) ───────────────────────────────
// Deterministic presentation of already-stored official SkillBridge metadata.
// No invention, no enrichment — only cleaning, hiding placeholders, and
// suppressing content that substantially duplicates another section.

const _GENERIC_EMPTY = new Set([
  '', 'none', 'n/a', 'na', 'unknown', 'tbd', 'to be determined', 'not specified',
]);
const CORE_BRANCHES = ['Air Force', 'Army', 'Coast Guard', 'Marine Corps', 'Navy', 'Space Force'];

function _clean(v) {
  if (v == null) return null;
  const s = String(v).trim();
  if (_GENERIC_EMPTY.has(s.toLowerCase())) return null;
  return s;
}

function _norm(s) {
  return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

// True when two blocks of text are substantially the same content.
function _isDuplicate(a, b) {
  const na = _norm(a), nb = _norm(b);
  if (!na || !nb) return false;
  if (na === nb) return true;
  if (na.length > 30 && nb.length > 30 && (na.includes(nb) || nb.includes(na))) return true;
  const sa = new Set(na.split(' ')), sb = new Set(nb.split(' '));
  let inter = 0;
  sa.forEach((w) => { if (sb.has(w)) inter += 1; });
  const uni = new Set([...sa, ...sb]).size;
  return uni > 0 && inter / uni > 0.7;
}

function _branches(arr) {
  if (!Array.isArray(arr)) return null;
  const list = arr.map((b) => String(b).trim()).filter((b) => b && b.toLowerCase() !== 'unknown');
  if (!list.length) return null;
  const core = list.filter((b) => CORE_BRANCHES.includes(b));
  if (core.length >= CORE_BRANCHES.length) return 'All branches';
  return list.join(', ');
}

// Location/states row: only surface when it adds info beyond the header city/state
// (i.e. nationwide availability or multiple states).
function _statesRow(v) {
  const s = _clean(v);
  if (!s) return null;
  const low = s.toLowerCase();
  if (low === 'all' || low.includes('nationwide')) return 'Nationwide (all states)';
  if (s.includes(',')) return s;
  return null;
}

// Build the ordered, cleaned section model for a SkillBridge job detail page.
export function skillbridgeSections(job) {
  const meta = job.skillbridge_meta || {};

  const about = _clean(meta.summary);

  // Responsibilities from the raw description — hidden if it duplicates About.
  const rawDesc = job.description;
  const respBlocks = (rawDesc && !(about && _isDuplicate(rawDesc, about)))
    ? formatSkillbridgeDescription(rawDesc)
    : null;

  const eligibility = _clean(meta.other_eligibility);

  let targetMocs = _clean(meta.target_mocs);
  if (targetMocs && targetMocs.toLowerCase() === 'all') targetMocs = null;

  const industries = _clean(meta.industries);
  const careerFields = industries
    ? industries.split(';').map((s) => s.trim()).filter(Boolean)
    : null;

  // Program details rows (only non-empty values).
  const program = [];
  const duration = _clean(meta.duration);
  if (duration && duration.toLowerCase() !== 'under review') {
    program.push({ key: 'duration', label: 'Duration', value: duration });
  }
  const delivery = _clean(meta.delivery_method);
  if (delivery) program.push({ key: 'delivery', label: 'Delivery method', value: delivery });
  const states = _statesRow(meta.location_states);
  if (states) program.push({ key: 'states', label: 'Availability', value: states });
  const installation = _clean(meta.installation);
  if (installation) program.push({ key: 'installation', label: 'Installation', value: installation });
  const branches = _branches(meta.branches);
  if (branches) program.push({ key: 'branches', label: 'Eligible branches', value: branches });

  // Additional notes — only when substantive and not duplicative of other sections.
  let notes = _clean(meta.other_notes);
  if (notes) {
    const dup = [about, rawDesc, eligibility].some((t) => t && _isDuplicate(notes, t));
    if (dup) notes = null;
  }

  const contact = {
    name: _clean(meta.contact_name),
    emails: Array.isArray(meta.contact_emails) ? meta.contact_emails.filter(Boolean) : [],
    urls: Array.isArray(meta.contact_urls) ? meta.contact_urls.filter(Boolean) : [],
  };

  return { about, respBlocks, eligibility, targetMocs, careerFields, program, notes, contact };
}

const _SB_HEADINGS = [
  'Key Responsibilities', 'Responsibilities', 'Preferred Qualifications',
  'Qualifications', 'Requirements', "What You'll Gain", 'What You\u2019ll Gain',
  'About the Program', 'About the Role', 'Overview', 'Benefits', 'Skills', 'Experience',
];

// Turn flattened imported SkillBridge description text into structured blocks
// ({type:'heading'|'para'|'list'}). ONLY deterministic separators actually present in
// the source are used: known section-heading strings (promoted ONLY at a real block
// boundary so inline "Experience:" labels inside bullets are NOT mistaken for headings),
// • bullet markers, and existing line breaks. Missing spaces AFTER sentence punctuation
// glued to the next capital are restored. Wording is never changed; nothing is invented.
export function formatSkillbridgeDescription(raw) {
  if (!raw || !String(raw).trim()) return null;
  let text = String(raw);

  // Restore a space after . ? ! glued directly to a following capital ("career.Key").
  text = text.replace(/([a-z)\]])([.?!])([A-Z])/g, '$1$2 $3');
  // Put every bullet marker on its own line.
  text = text.replace(/\s*•\s*/g, '\n• ');

  const escaped = _SB_HEADINGS.map((h) => h.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
  // Promote a heading only at a real boundary: start, newline, or after . : ! ?
  const headingRe = new RegExp('(^|[\\n.:!?])[ \\t]*(' + escaped + ')[ \\t]*:', 'g');
  text = text.replace(headingRe, '$1\n@@H@@$2\n');

  const lines = text.split('\n').map((l) => l.replace(/[ \t]+/g, ' ').trim()).filter(Boolean);

  const blocks = [];
  let bullets = [];
  const flush = () => { if (bullets.length) { blocks.push({ type: 'list', items: bullets }); bullets = []; } };
  for (const line of lines) {
    if (line.startsWith('@@H@@')) { flush(); blocks.push({ type: 'heading', text: line.slice(5).trim() }); }
    else if (line.startsWith('•')) { bullets.push(line.replace(/^•\s*/, '').trim()); }
    else { flush(); blocks.push({ type: 'para', text: line }); }
  }
  flush();
  return blocks;
}
